import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, friendlyError } from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { SourceLevelTag } from "../lib/ui";
import { Send, ExternalLink, ThumbsUp, ThumbsDown, Sparkles, ShieldQuestion, FileText } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Quali novità FDA sull'aseptic processing sono rilevanti per il mio sito?",
  "Questa modifica richiede una variazione nell'UE?",
  "Confronta i requisiti EU e USA sulla data integrity.",
  "Cosa richiede Annex 1 in materia di Contamination Control Strategy?",
];

const STRENGTH_COLOR = { ALTA: "#16A34A", MEDIA: "#D97706", LIMITATA: "#EA580C", NESSUNA: "#94A3B8" };

function SourceCard({ s }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid={`copilot-source-${s.n}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono font-semibold text-slate-700">[{s.n}]</span>
        {s.kind === "public"
          ? <SourceLevelTag level={s.source_level} />
          : <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-mono text-[0.6rem]">AZIENDALE</span>}
        <span className="font-medium text-slate-600">{s.authority}</span>
      </div>
      <div className="text-slate-700 font-medium leading-snug">{s.title}</div>
      <div className="text-slate-400 mt-1 font-mono text-[0.65rem]">{s.version} · {(s.date || "").slice(0, 10)}</div>
      {s.url && (
        <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 text-blue-600 hover:underline">
          <ExternalLink size={11} /> Apri fonte
        </a>
      )}
    </div>
  );
}

export default function Copilot() {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sessionId = useRef("copilot_" + Date.now());

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || busy) return;
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/copilot", { query: question, session_id: sessionId.current });
      setMsgs((m) => [...m, { role: "assistant", ...data }]);
    } catch (e) {
      toast.error(friendlyError(e));
      setMsgs((m) => [...m, { role: "assistant", answer: "Errore nella richiesta.", sources: [] }]);
    } finally { setBusy(false); }
  };

  const feedback = async (query_id, fb) => {
    if (!query_id) return;
    try { await api.post("/ai-feedback", { query_id, feedback: fb }); toast.success("Grazie per il feedback"); } catch {}
  };

  return (
    <div className="ri-fade-in flex flex-col h-[calc(100vh-7rem)]">
      <PageHeader overline="AI · RAG con fonti verificabili" title="Regulatory Copilot"
        subtitle="Ogni risposta usa il retrieval sulla knowledge base e cita le fonti. Distingue requisito, guidance, aspettativa ispettiva e interpretazione AI." />

      <div className="flex-1 overflow-y-auto rounded-xl bg-white border border-slate-200 p-5 space-y-5">
        {msgs.length === 0 && (
          <div className="text-center py-10">
            <Sparkles size={28} className="text-blue-600 mx-auto mb-3" />
            <p className="text-slate-500 text-sm mb-5">Poni una domanda regolatoria. Il Copilot risponde solo dalle fonti disponibili.</p>
            <div className="grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => ask(s)} data-testid={`copilot-suggestion-${i}`}
                  className="text-left text-sm px-4 py-3 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {msgs.map((m, i) => m.role === "user" ? (
          <div key={i} className="flex justify-end">
            <div className="bg-slate-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%] text-sm">{m.text}</div>
          </div>
        ) : (
          <div key={i} className="flex gap-3" data-testid={`copilot-answer-${i}`}>
            <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
              <Sparkles size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="prose prose-sm max-w-none text-slate-700 prose-headings:font-head">
                <ReactMarkdown>{m.answer}</ReactMarkdown>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldQuestion size={13} className="text-slate-400" />
                  Forza evidenza: <b style={{ color: STRENGTH_COLOR[m.evidence_strength] || "#64748B" }}>{m.evidence_strength}</b>
                </span>
                <span className="text-slate-400">Confidence AI: <b className="text-slate-600">{m.confidence}%</b></span>
                <button onClick={() => feedback(m.query_id, "Accepted")} className="text-slate-400 hover:text-green-600" data-testid={`copilot-thumbup-${i}`}><ThumbsUp size={14} /></button>
                <button onClick={() => feedback(m.query_id, "Incorrect")} className="text-slate-400 hover:text-red-600"><ThumbsDown size={14} /></button>
              </div>
              {m.sources?.length > 0 && (
                <>
                  <div className="ri-overline mt-4 mb-2 flex items-center gap-1"><FileText size={12} /> Fonti citate</div>
                  <div className="grid sm:grid-cols-2 gap-2">{m.sources.map((s) => <SourceCard key={s.n} s={s} />)}</div>
                </>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="flex gap-3 items-center text-slate-400 text-sm"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /> Il Copilot sta analizzando le fonti...</div>}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-3 flex gap-2">
        <input data-testid="copilot-input" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Scrivi una domanda regolatoria..."
          className="flex-1 px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
        <button type="submit" disabled={busy} data-testid="copilot-submit-btn"
          className="px-5 rounded-xl bg-blue-700 text-white hover:bg-blue-800 transition-colors disabled:opacity-60 flex items-center gap-2">
          <Send size={17} />
        </button>
      </form>
    </div>
  );
}
